import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BrandLogo } from "@/components/brand-logo";
import { Container } from "@/components/ui/layout";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type MarketplacePlatform = {
  id: string;
  name: string;
  logoUrl: string;
  available: boolean;
};

export function SiteFooter() {
  const { data } = useQuery({
    queryKey: ["catalog", "marketplaces"],
    queryFn: () =>
      api<{ platforms: MarketplacePlatform[] }>("/api/catalog/marketplaces"),
    staleTime: 60_000,
  });

  const platforms = data?.platforms ?? [];

  return (
    <footer className="border-t border-border bg-secondary">
      <Container className="grid gap-10 py-16 md:grid-cols-4">
        <div className="md:col-span-2">
          <BrandLogo size="md" to="/" houseLabel="" />
          <p className="mt-5 max-w-md font-display text-base italic text-primary">
            Made with love from traditional recipes.
          </p>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-muted">
            Homemade Chutneys & Traditional Aachar — no preservatives, freshly
            prepared.
          </p>
          <div className="mt-6">
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted">
              Also available on
            </p>
            <ul className="mt-2 flex flex-nowrap items-center gap-2 overflow-x-auto pb-1">
              {platforms.map((platform) => (
                <li
                  key={platform.id}
                  title={`${platform.name} — ${platform.available ? "Available" : "Coming soon"}`}
                  className="flex shrink-0 items-center gap-1.5 rounded-md border border-border/80 bg-white px-1.5 py-1"
                >
                  <img
                    src={platform.logoUrl}
                    alt={platform.name}
                    className="h-6 w-auto max-w-[4.5rem] object-contain"
                    loading="lazy"
                  />
                  <span
                    className={cn(
                      "whitespace-nowrap text-[9px] font-medium uppercase tracking-wide",
                      platform.available ? "text-primary" : "text-muted",
                    )}
                  >
                    {platform.available ? "Live" : "Soon"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-foreground">
            Explore
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted">
            <li>
              <Link to="/shop" className="hover:text-foreground cursor-pointer">
                Shop
              </Link>
            </li>
            <li>
              <Link
                to="/guides/homemade-chutneys"
                className="hover:text-foreground cursor-pointer"
              >
                Chutney guide
              </Link>
            </li>
            <li>
              <Link
                to="/shop?category=chutney-house"
                className="hover:text-foreground cursor-pointer"
              >
                Chutney House
              </Link>
            </li>
            <li>
              <Link
                to="/shop?category=aachar-house"
                className="hover:text-foreground cursor-pointer"
              >
                Aachar House
              </Link>
            </li>
            <li>
              <Link
                to="/account"
                className="hover:text-foreground cursor-pointer"
              >
                My account
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-foreground">
            Policies
          </p>
          <ul className="mt-4 space-y-2 text-sm text-muted">
            <li>
              <Link
                to="/policies/privacy"
                className="hover:text-foreground cursor-pointer"
              >
                Privacy
              </Link>
            </li>
            <li>
              <Link
                to="/policies/terms"
                className="hover:text-foreground cursor-pointer"
              >
                Terms &amp; Conditions
              </Link>
            </li>
            <li>
              <Link
                to="/policies/shipping"
                className="hover:text-foreground cursor-pointer"
              >
                Delivery
              </Link>
            </li>
            <li>
              <Link
                to="/policies/refund"
                className="hover:text-foreground cursor-pointer"
              >
                Refund
              </Link>
            </li>
            <li>
              <Link
                to="/policies/returns"
                className="hover:text-foreground cursor-pointer"
              >
                Returns
              </Link>
            </li>
          </ul>
        </div>
      </Container>
      <div className="border-t border-border/80">
        <Container className="flex flex-col gap-2 py-6 text-xs text-muted md:flex-row md:justify-between">
          <p>
            © {new Date().getFullYear()} Nani&apos;s Recipe under SP Labels
            (subsidiary). All rights reserved.
          </p>
          <p>Crafted for trust, simplicity, and handcrafted authenticity.</p>
        </Container>
      </div>
    </footer>
  );
}
